import { AdditionalMeterDetail, TprData } from "../../types/components.types";
import {
  MultiMpanResult,
  TableData,
  TableColumn,
  TableRow,
  TableCellValue,
} from "../../types/services.types";

// Check if the meter is half-hourly (HH) based on the meter's profile class.
export const isHalfHourlyMeter = (additionalDetail: AdditionalMeterDetail[]): boolean => {
  // Return early if the additionalDetail array is missing or empty.
  // We need to override the linter to ensure that this check is performed properly
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!additionalDetail || additionalDetail.length === 0) return false;

  // Check if the profile class is equal to zero. This means that the meter is half hourly (HH).
  return additionalDetail[0].additional_detail_profile_class === "0";
};

// Format field names for display e.g.,"metering_point_address_line_1" formats to "Metering Point Address Line 1"
export const formatFieldName = (field: string): string => {
  return field
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Check if any results contain half-hourly meters.
export const hasHalfHourlyMeters = (results: MultiMpanResult[]): boolean => {
  return results.some((result) => result.success && result.meterDetails?.is_half_hourly === true);
};

// Filter results to only include successful half-hourly meters.
export const getHalfHourlyResults = (results: MultiMpanResult[]): MultiMpanResult[] => {
  return results.filter((result) => result.success && result.meterDetails?.is_half_hourly === true);
};

export const flattenMeterDetailsForTable = (results: MultiMpanResult[]): TableData => {
  // Build columns from the results.
  const columns: TableColumn[] = results.map((result) => ({
    mpan: result.mpan,
    hasError: !result.success,
  }));

  // Extract all unique field names from the successful results.
  const fieldSet = new Set<string>();
  results.forEach((result) => {
    if (result.success && result.meterDetails) {
      console.log(`Processing meter details: ${String(result.meterDetails.mpan_top_line)}`);
      Object.keys(result.meterDetails).forEach((key) => {
        // Skip nested arrays/objects and MPAN (as the MPAN/s are already in column headers).
        if (
          key !== "additional_detail" &&
          key !== "tpr_data" &&
          key !== "hh_aq_calc" &&
          key !== "mpan"
        ) {
          console.log(`Adding field to table: ${key}`);
          fieldSet.add(key);
        }
      });
    }
  });

  // Add the flattened fields for additional_detail.
  results.forEach((result) => {
    if (result.success && result.meterDetails?.additional_detail[0]) {
      Object.keys(result.meterDetails.additional_detail[0]).forEach((key) => {
        fieldSet.add(`additional_detail_${key}`);
      });
    }
  });

  // Add the flattened fields for tpr_data.
  results.forEach((result) => {
    if (result.success && result.meterDetails?.tpr_data) {
      result.meterDetails.tpr_data.forEach((_, index) => {
        fieldSet.add(`tpr_data_${String(index)}_tpr_eac`);
        fieldSet.add(`tpr_data_${String(index)}_eac_tpr`);
        fieldSet.add(`tpr_data_${String(index)}_eac_efd`);
      });
    }
  });

  // Add TAQ field if there are any HH meters.
  if (hasHalfHourlyMeters(results)) {
    fieldSet.add("taq");
  }

  // Add combined full address field.
  fieldSet.add("full_address");

  const fields = Array.from(fieldSet);

  // Build the table rows.
  const rows: TableRow[] = fields.map((field) => {
    const values: TableCellValue[] = results.map((result) => {
      if (!result.success) {
        // Display an error for the failed MPANs.
        return {
          display: result.error ?? "Error",
          isError: true,
        };
      }

      if (!result.meterDetails) {
        return {
          display: "",
          isError: false,
        };
      }

      // Handle nested additional_detail fields
      if (field.startsWith("additional_detail_")) {
        const detailKey = field.replace("additional_detail_", "");
        const value =
          result.meterDetails.additional_detail[0]?.[detailKey as keyof AdditionalMeterDetail];
        return {
          display: value || "",
          isError: false,
        };
      }

      // Handle nested tpr_data fields
      if (field.startsWith("tpr_data_")) {
        const match = /tpr_data_(\d+)_(.*)/.exec(field);
        if (match) {
          const index = parseInt(match[1]);
          const tprKey = match[2];
          // Meters can have different numbers of TPR records (and the API can return null values), so a meter may have no value for this field.
          const tprRecord = result.meterDetails.tpr_data[index] as TprData | undefined;
          const value = tprRecord?.[tprKey as keyof TprData] as string | number | null | undefined;
          return {
            display: value?.toString() ?? "",
            isError: false,
            isNumeric: false,
          };
        }
      }

      // Handle TAQ field for half-hourly meters
      if (field === "taq") {
        // Only show TAQ value for half-hourly meters (empty for non-HH).
        if (
          result.meterDetails.is_half_hourly &&
          result.meterDetails.hh_aq_calc?.taq !== undefined
        ) {
          return {
            display: result.meterDetails.hh_aq_calc.taq.toString(),
            isError: false,
          };
        }
        return {
          display: "",
          isError: false,
        };
      }

      // Handle combined full address field.
      if (field === "full_address") {
        const addressParts = [
          result.meterDetails.metering_point_address_line_1,
          result.meterDetails.metering_point_address_line_2,
          result.meterDetails.metering_point_address_line_3,
          result.meterDetails.metering_point_address_line_4,
          result.meterDetails.metering_point_address_line_5,
          result.meterDetails.metering_point_address_line_6,
          result.meterDetails.metering_point_address_line_7,
          result.meterDetails.metering_point_address_line_8,
          result.meterDetails.metering_point_address_line_9,
          result.meterDetails.post_code,
        ];
        // Filter out empty/null/undefined values and join with comma and space.
        const fullAddress = addressParts.filter((part) => part !== "").join(", ");
        return {
          display: fullAddress,
          isError: false,
        };
      }

      // Handle regular fields
      const value = result.meterDetails[field as keyof typeof result.meterDetails];

      if (value === null || value === undefined || value === "") {
        return {
          display: "",
          isError: false,
        };
      }

      if (typeof value === "object") {
        return {
          display: JSON.stringify(value),
          isError: false,
        };
      }

      return {
        display: value.toString(),
        isError: false,
        isNumeric: false,
      };
    });

    return {
      field:
        field === "taq"
          ? "TAQ"
          : field === "full_address"
            ? "Full Address"
            : formatFieldName(field),
      values,
    };
  });

  return { columns, rows };
};

// Generate TAQ-only table data for 5+ MPANs with HH meters.
// Only includes HH meters as columns and TAQ as the single row.
export const flattenTaqOnlyForTable = (results: MultiMpanResult[]): TableData => {
  // Filter to only half-hourly meters.
  const hhResults = getHalfHourlyResults(results);

  // Build columns from HH results only.
  const columns: TableColumn[] = hhResults.map((result) => ({
    mpan: result.mpan,
    hasError: false,
  }));

  // Build single TAQ row.
  const taqRow: TableRow = {
    field: "TAQ",
    values: hhResults.map((result) => {
      const taqValue = result.meterDetails?.hh_aq_calc?.taq;
      if (taqValue !== undefined) {
        return {
          display: taqValue.toString(),
          isError: false,
        };
      }
      // HH meter but TAQ fetch failed.
      return {
        display: "Unavailable",
        isError: true,
      };
    }),
  };

  return {
    columns,
    rows: [taqRow],
  };
};
